import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Wrench,
  Plus,
  ClipboardList,
  LogOut,
  Search,
  User,
  ChevronDown,
  Sparkles,
  MessageSquare,
  HelpCircle,
} from 'lucide-react';
import clsx from 'clsx';
import { useAuth } from '@shared/authContext';
import { canAccessAdvancedSettings } from '@shared/permissions';
import { useClientModal } from '@shared/clientModalContext';
import {
  ZONES,
  DISPATCH_VIEWS,
  getNavForZone,
  isNavItemActive,
} from '../utils/zoneNav';
import { useShell } from './ShellContext';
import { useTutorial } from '../features/tutorial/TutorialContext';

export default function CommandBar({ onOpenPartsModal }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { openCreateModal } = useClientModal();
  const {
    activeZone,
    setActiveZone,
    dispatchView,
    setDispatchView,
    setCommandPaletteOpen,
    setAssistantOpen,
    openMessageInbox,
    badgeCounts,
  } = useShell();
  const { openHub } = useTutorial();

  const zoneNav = getNavForZone(activeZone).filter(
    (item) => !item.requiresAdvancedSettings || canAccessAdvancedSettings(user)
  );

  const isDispatchWorkspace = location.pathname === '/dispatch';

  return (
    <>
      {/* Primary bar: branding, three main modes, global actions */}
      <header className="hub-command-bar">
        <div className="flex items-center gap-2 flex-shrink-0">
          <Wrench className="h-5 w-5 text-white" />
          <div className="hidden sm:block">
            <span className="text-sm font-semibold text-white">K&R Hub</span>
            <span className="text-2xs text-primary-200 ml-1.5">2.0</span>
          </div>
        </div>

        <div className="flex items-center bg-primary-800/60 rounded-md p-0.5 border border-primary-500/40" data-tour-id="zone-switcher">
          {Object.values(ZONES).map((zone) => (
            <button
              key={zone.id}
              type="button"
              onClick={() => setActiveZone(zone.id)}
              data-tour-id={`zone-${zone.id}`}
              className={clsx(
                'px-3 py-1 text-xs font-medium rounded transition-colors',
                activeZone === zone.id
                  ? 'bg-white text-primary-700 shadow-panel'
                  : 'text-primary-100 hover:text-white hover:bg-white/10'
              )}
              title={zone.description}
            >
              {zone.label}
            </button>
          ))}
        </div>

        <div className="flex-1" />

        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            type="button"
            onClick={() => openMessageInbox()}
            data-tour-id="messages-btn"
            className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 text-xs text-primary-100 border border-primary-500/40 rounded-md hover:bg-white/10 hover:text-white"
            title="Messages"
          >
            <MessageSquare className="h-3.5 w-3.5" />
            <span>Messages</span>
            {badgeCounts.messages > 0 && (
              <span className="min-w-[1rem] h-4 px-1 rounded text-2xs font-semibold tabular-nums inline-flex items-center justify-center bg-white text-primary-700">
                {badgeCounts.messages}
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={() => setAssistantOpen(true)}
            data-tour-id="assistant-btn"
            className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 text-xs text-primary-100 border border-primary-500/40 rounded-md hover:bg-white/10 hover:text-white"
            title="Hub Assistant"
          >
            <Sparkles className="h-3.5 w-3.5" />
            <span>Assistant</span>
            <kbd className="text-2xs bg-primary-800/60 text-primary-100 px-1 rounded border border-primary-500/40">⌘J</kbd>
          </button>

          <button
            type="button"
            onClick={() => setCommandPaletteOpen(true)}
            data-tour-id="command-palette-btn"
            className="hidden sm:inline-flex items-center gap-2 px-2.5 py-1 text-xs text-primary-100 border border-primary-500/40 rounded-md hover:bg-white/10 hover:text-white"
          >
            <Search className="h-3.5 w-3.5" />
            <span>Search</span>
            <kbd className="text-2xs bg-primary-800/60 text-primary-100 px-1 rounded border border-primary-500/40">⌘K</kbd>
          </button>

          <button
            type="button"
            onClick={openHub}
            data-tour-id="tutorial-help-btn"
            className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 text-xs text-primary-100 border border-primary-500/40 rounded-md hover:bg-white/10 hover:text-white"
            title="Tutorial Hub"
          >
            <HelpCircle className="h-3.5 w-3.5" />
            <span>Help</span>
          </button>

          <button
            type="button"
            onClick={openCreateModal}
            className="p-1.5 rounded-md hover:bg-white/10 text-primary-100 hover:text-white"
            title="Quick Add Customer"
          >
            <Plus className="h-4 w-4" />
          </button>

          <button
            type="button"
            onClick={() => openMessageInbox()}
            className="sm:hidden p-1.5 rounded-md hover:bg-white/10 text-primary-100 hover:text-white relative"
            title="Messages"
          >
            <MessageSquare className="h-4 w-4" />
            {badgeCounts.messages > 0 && (
              <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-white" />
            )}
          </button>

          <button
            type="button"
            onClick={() => setAssistantOpen(true)}
            className="sm:hidden p-1.5 rounded-md hover:bg-white/10 text-primary-100 hover:text-white"
            title="Hub Assistant"
          >
            <Sparkles className="h-4 w-4" />
          </button>

          <button
            type="button"
            onClick={openHub}
            className="sm:hidden p-1.5 rounded-md hover:bg-white/10 text-primary-100 hover:text-white"
            title="Tutorial Hub"
          >
            <HelpCircle className="h-4 w-4" />
          </button>

          <button
            type="button"
            onClick={onOpenPartsModal}
            className="p-1.5 rounded-md hover:bg-white/10 text-primary-100 hover:text-white relative"
            title="Request Part"
          >
            <ClipboardList className="h-4 w-4" />
            {badgeCounts.parts > 0 && (
              <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-red-400" />
            )}
          </button>

          <div className="hidden sm:flex items-center gap-1 pl-1 border-l border-primary-500/40 ml-1">
            <button
              type="button"
              onClick={() => navigate('/profile')}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-primary-100 hover:bg-white/10 hover:text-white rounded-md"
            >
              <User className="h-3.5 w-3.5" />
              {user?.firstName}
              <ChevronDown className="h-3 w-3 text-primary-300" />
            </button>
            <button
              type="button"
              onClick={logout}
              className="p-1.5 rounded-md hover:bg-white/10 text-primary-200 hover:text-white"
              title="Logout"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Secondary bar: sub-pages for the active zone (or dispatch view switcher) */}
      <div className="hub-subnav-bar top-command-bar">
        {activeZone === 'dispatch' && isDispatchWorkspace && (
          <div className="flex items-center bg-surface-secondary rounded-md p-0.5 border border-surface-border" data-tour-id="dispatch-views">
            {Object.values(DISPATCH_VIEWS).map((view) => {
              const Icon = view.icon;
              return (
                <button
                  key={view.id}
                  type="button"
                  onClick={() => setDispatchView(view.id)}
                  data-tour-id={`dispatch-view-${view.id}`}
                  className={clsx(
                    'inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded transition-colors',
                    dispatchView === view.id
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-600 hover:bg-surface-hover'
                  )}
                  title={`${view.label} (${view.shortcut})`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  <span className="hidden md:inline">{view.label}</span>
                </button>
              );
            })}
          </div>
        )}

        {zoneNav.length > 1 && !(activeZone === 'dispatch' && isDispatchWorkspace) && (
          <nav className="flex items-center gap-0.5 overflow-x-auto">
            {zoneNav.map((item) => {
              const Icon = item.icon;
              const active = isNavItemActive(item, location.pathname);
              const badge = item.badgeKey ? badgeCounts[item.badgeKey] : 0;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  data-tour-id={`nav-${item.href}`}
                  className={clsx(
                    'inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md whitespace-nowrap transition-colors',
                    active
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-600 hover:bg-surface-hover'
                  )}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {item.name}
                  {badge > 0 && (
                    <span className={clsx(
                      'min-w-[1rem] h-4 px-1 rounded text-2xs font-semibold tabular-nums inline-flex items-center justify-center',
                      active ? 'bg-white/20 text-white' : 'bg-primary-100 text-primary-700'
                    )}>
                      {badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>
        )}
      </div>
    </>
  );
}
